import { Fragment, useContext, useRef, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/outline";
import AuthContext from "../AuthContext";
import { apiFetch } from "../api/client";
import { useNotification } from "../components/Notifications";
import { mapApiErrorToNotification } from "../utils/notificationHelpers";
import { formatNairaInput } from "../utils/currencyInput";

export default function AddProduct({
  addProductModalSetting,
  handlePageUpdate,
  categories = [],
  vendors = [],
  initial = null,
}) {
  const authContext = useContext(AuthContext);
  const notify = useNotification();
  const [product, setProduct] = useState({
    userId: authContext.user,
    name: "",
    vendor: "",
    description: "",
    purchaseCost: "",
    salePrice: "",
    categoryId: "",
    vendorId: "",
    id: null,
  });
  const [localVendors, setLocalVendors] = useState(vendors || []);
  const [localCategories, setLocalCategories] = useState(categories || []);
  const [open, setOpen] = useState(true);
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [openVendorModal, setOpenVendorModal] = useState(false);
  const [newCategory, setNewCategory] = useState({ categoryName: "" });
  const [newVendor, setNewVendor] = useState({ vendorName: "", phoneNumber: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const cancelButtonRef = useRef(null);

  const handleInputChange = (key, value) => {
    setProduct({ ...product, [key]: value });
  };

  // Prefill when editing
  useEffect(() => {
    if (!initial) return;
    setProduct((p) => ({
      ...p,
      id: initial.id || initial.productId || initial.ProductId || initial._id || null,
      name: initial.name || initial.Name || "",
      description: initial.description || initial.Description || "",
      purchaseCost: formatNairaInput(initial.purchaseCost ?? initial.PurchaseCost ?? ""),
      salePrice: formatNairaInput(initial.salePrice ?? initial.salesPrice ?? initial.SalePrice ?? initial.SalesPrice ?? ""),
      categoryId: initial.categoryId || initial.CategoryId || initial.categoryId || null,
      vendorId: initial.vendorId || initial.VendorId || initial.vendorId || null,
    }));
    // populate local lists if passed
    if (initial.category || initial.categoryName) {
      setLocalCategories((prev) => {
        const exists = prev.find((c) => (c.categoryId || c.CategoryId) === (initial.categoryId || initial.CategoryId));
        if (exists) return prev;
        return [...prev, { categoryId: initial.categoryId || initial.CategoryId, categoryName: initial.category || initial.categoryName || initial.CategoryName }];
      });
    }
    if (initial.vendor || initial.vendorName) {
      setLocalVendors((prev) => {
        const exists = prev.find((v) => (v.vendorId || v.VendorId) === (initial.vendorId || initial.VendorId));
        if (exists) return prev;
        return [...prev, { vendorId: initial.vendorId || initial.VendorId, vendorName: initial.vendor || initial.vendorName || initial.VendorName }];
      });
    }
  }, [initial]);

  const handleNewCategoryChange = (key, value) => {
    setNewCategory({ ...newCategory, [key]: value });
  };

  const fetchCategories = async () => {
    try {
      const res = await apiFetch(`/api/category`);
      if (!res.ok) return;
      const data = await res.json().catch(() => null);
      const arr = Array.isArray(data) ? data : (data?.data || data?.Data || []);
      setLocalCategories(arr || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleNewVendorChange = (key, value) => {
    setNewVendor({ ...newVendor, [key]: value });
  };

  const fetchVendors = async () => {
    try {
      const res = await apiFetch(`/api/vendor`);
      if (!res.ok) return;
      const data = await res.json().catch(() => null);
      const arr = Array.isArray(data) ? data : (data?.data || data?.Data || []);
      setLocalVendors(arr || []);
    } catch (err) {
      console.error(err);
    }
  };

  const addProduct = async () => {
    try {
      if (isSubmitting) return;
      setIsSubmitting(true);
      // helper to sanitize numeric inputs (e.g. "3,000" -> 3000)
      const parseNumber = (v) => {
        if (v === null || v === undefined) return 0;
        const s = String(v).replace(/[^0-9.-]+/g, "");
        const n = parseFloat(s);
        return Number.isNaN(n) ? 0 : n;
      };

      // build payload matching backend DTO
      const payload = {
        Name: product.name,
        Description: product.description,
        PurchaseCost: parseNumber(product.purchaseCost),
        SalePrice: parseNumber(product.salePrice),
        CategoryId: product.categoryId || null,
        VendorId: product.vendorId || null,
      };

      // if editing, call PUT
      let res;
      if (product.id) {
        res = await apiFetch(`/api/product/${product.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        res = await apiFetch(`/api/product`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      if (!res.ok) {
        const parsed = await res.json().catch(() => null);
        const note = mapApiErrorToNotification(parsed, res.status);
        notify("error", note);
        return;
      }
      notify("success", { title: product.id ? "Product updated" : "Product added", text: product.id ? "Product updated successfully." : "Product added successfully." });
      handlePageUpdate();
      addProductModalSetting();
    } catch (err) {
      console.error(err);
      notify("error", { title: "Network error", text: "Could not reach server." });
    } finally {
      setIsSubmitting(false);
    }
  };
  const addCategory = async () => {
    try {
      if (!newCategory.categoryName || !newCategory.categoryName.trim()) {
        notify("error", { title: "Validation", text: "Category name is required." });
        return;
      }

      const payload = { CategoryName: newCategory.categoryName };
      const res = await apiFetch(`/api/category`, { method: "POST", body: JSON.stringify(payload) });
      if (!res.ok) {
        const parsed = await res.json().catch(() => null);
        const note = mapApiErrorToNotification(parsed, res.status);
        notify("error", note);
        return;
      }

      const parsed = await res.json().catch(() => null);
      const created = parsed?.data || parsed?.Data || parsed;
      if (created) {
        const c = {
          categoryId: created.categoryId || created.CategoryId || created.categoryId,
          categoryName: created.categoryName || created.CategoryName || created.categoryName,
        };
        setLocalCategories((prev) => [...prev, c]);
        setProduct((p) => ({ ...p, categoryId: c.categoryId }));
      }

      setNewCategory({ categoryName: "" });
      setOpenCategoryModal(false);
      notify("success", { title: "Category added", text: "Category added successfully." });
    } catch (err) {
      console.error(err);
      notify("error", { title: "Network error", text: "Could not reach server." });
    }
  };

  const addVendor = async () => {
    try {
      if (!newVendor.vendorName || !newVendor.vendorName.trim()) {
        notify("error", { title: "Validation", text: "Vendor name is required." });
        return;
      }

      const payload = {
        VendorName: newVendor.vendorName,
        PhoneNumber: newVendor.phoneNumber || "",
      };

      const res = await apiFetch(`/api/vendor`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const parsed = await res.json().catch(() => null);
        const note = mapApiErrorToNotification(parsed, res.status);
        notify("error", note);
        return;
      }

      const parsed = await res.json().catch(() => null);
      // extract created vendor from common response shapes
      const created = parsed?.data || parsed?.Data || parsed;
      if (created) {
        // ensure vendorId and vendorName keys exist (case variations)
        const v = {
          vendorId: created.vendorId || created.VendorId || created.vendorId,
          vendorName: created.vendorName || created.VendorName || created.vendorName,
          phoneNumber: created.phoneNumber || created.PhoneNumber || created.phoneNumber,
        };
        setLocalVendors((prev) => [...prev, v]);
        setProduct((p) => ({ ...p, vendorId: v.vendorId }));
      }

      setNewVendor({ vendorName: "", phoneNumber: "" });
      setOpenVendorModal(false);
      notify("success", { title: "Vendor added", text: "Vendor added successfully." });
    } catch (err) {
      console.error(err);
      notify("error", { title: "Network error", text: "Could not reach server." });
    }
  };

  return (
    <>
      {/* Product Modal */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" initialFocus={cancelButtonRef} onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                        <PlusIcon className="h-6 w-6 text-blue-400" aria-hidden="true" />
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left ">
                        <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 ">
                          Add Product
                        </Dialog.Title>
                          <form action="#">
                            <div className="grid gap-4 mb-4 sm:grid-cols-2">
                              {/* Product ID removed - server generates IDs */}

                              {/* Name */}
                              <div>
                                <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-900">
                                  Name
                                </label>
                                <input
                                  type="text"
                                  name="name"
                                  id="name"
                                  value={product.name}
                                  onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                  placeholder="Ex. Apple iMac"
                                  disabled={isSubmitting}
                                />
                              </div>

                              {/* Purchase Cost */}
                              <div>
                                <label htmlFor="purchaseCost" className="block mb-2 text-sm font-medium text-gray-900">
                                  Purchase Cost
                                </label>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  name="purchaseCost"
                                  id="purchaseCost"
                                  value={product.purchaseCost}
                                  onChange={(e) => handleInputChange(e.target.name, formatNairaInput(e.target.value))}
                                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                  placeholder="Ex. ₦1,000"
                                  disabled={isSubmitting}
                                />
                              </div>

                              {/* Sale Price */}
                              <div>
                                <label htmlFor="salePrice" className="block mb-2 text-sm font-medium text-gray-900">
                                  Sale Price
                                </label>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  name="salePrice"
                                  id="salePrice"
                                  value={product.salePrice}
                                  onChange={(e) => handleInputChange(e.target.name, formatNairaInput(e.target.value))}
                                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                  placeholder="Ex. ₦1,200"
                                  disabled={isSubmitting}
                                />
                              </div>

                              {/* Vendor */}
                              <div>
                                <label htmlFor="vendor" className="block mb-2 text-sm font-medium text-gray-900">
                                  Vendor
                                </label>
                                 <div className="flex gap-2">
                                  <select
                                    name="vendorId"
                                    value={product.vendorId}
                                    onChange={(e) => handleInputChange("vendorId", e.target.value)}
                                    onFocus={fetchVendors}
                                    onClick={fetchVendors}
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={isSubmitting}
                                  >
                                    <option value="">Select Vendor</option>
                                    {localVendors.map((ven) => (
                                      <option key={ven.vendorId || ven.vendorId} value={ven.vendorId || ven.vendorId}>
                                        {ven.vendorName || ven.vendorName}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    className="px-2 py-1 bg-blue-600 text-white text-sm rounded-md h-10 self-end"
                                    onClick={() => {
                                      fetchVendors();
                                      setOpenVendorModal(true);
                                    }}
                                  >
                                  Add+
                                  </button>
                                </div>
                              </div>

                              {/* Category */}
                              <div>
                                <label htmlFor="category" className="block mb-2 text-sm font-medium text-gray-900">
                                  Category
                                </label>
                                <div className="flex gap-2">
                                  <select
                                    name="categoryId"
                                    value={product.categoryId}
                                    onChange={(e) => handleInputChange("categoryId", e.target.value)}
                                    onFocus={fetchCategories}
                                    onClick={fetchCategories}
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={isSubmitting}
                                  >
                                    <option value="">Select Category</option>
                                    {localCategories.map((cat) => (
                                      <option key={cat.categoryId || cat.categoryId} value={cat.categoryId || cat.categoryId}>
                                        {cat.categoryName || cat.categoryName}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    className="px-2 py-1 bg-blue-600 text-white text-sm rounded-md h-10 self-end"
                                    onClick={() => setOpenCategoryModal(true)}
                                  >
                                  Add+
                                  </button>
                                </div>
                              </div>

                              {/* Description */}
                              <div className="sm:col-span-2">
                                <label htmlFor="description" className="block mb-2 text-sm font-medium text-gray-900">
                                  Description
                                </label>
                                <textarea
                                  id="description"
                                  rows="3"
                                  name="description"
                                  value={product.description}
                                  onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                                  className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                  placeholder="Write a description..."
                                  disabled={isSubmitting}
                                />
                              </div>
                            </div>
                          </form>

                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                      onClick={addProduct}
                      disabled={isSubmitting}
                      aria-busy={isSubmitting}
                    >
                      {isSubmitting && (
                        <svg className="animate-spin -ml-2 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                      )}
                      {isSubmitting ? (product.id ? "Updating..." : "Adding...") : (product.id ? "Update Product" : "Add Product")}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => addProductModalSetting()}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Nested Category Modal */}
      <Transition.Root show={openCategoryModal} as={Fragment}>
        <Dialog as="div" className="relative z-20" initialFocus={cancelButtonRef} onClose={setOpenCategoryModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:w-full sm:max-w-md">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">Add New Category</Dialog.Title>
                    <div className="mt-2">
                      <label className="block mt-2 mb-2 text-sm font-medium text-gray-900">Category Name</label>
                      <input
                        type="text"
                        value={newCategory.categoryName}
                        onChange={(e) => handleNewCategoryChange("categoryName", e.target.value)}
                        className="block w-full p-2.5 text-sm border border-gray-300 rounded-lg"
                        placeholder="Ex. Electronics"
                      />
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 sm:ml-3 sm:w-auto"
                      onClick={addCategory}
                    >
                      Add Category
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => setOpenCategoryModal(false)}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

       {/* Nested Vendor Modal */}
      <Transition.Root show={openVendorModal} as={Fragment}>
        <Dialog as="div" className="relative z-20" initialFocus={cancelButtonRef} onClose={setOpenVendorModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:w-full sm:max-w-md">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">Add New Vendor</Dialog.Title>
                    <div className="mt-2">
                      <label className="block mt-2 mb-2 text-sm font-medium text-gray-900">Vendor Name</label>
                      <input
                        type="text"
                        value={newVendor.vendorName}
                        onChange={(e) => handleNewVendorChange("vendorName", e.target.value)}
                        className="block w-full p-2.5 text-sm border border-gray-300 rounded-lg"
                        placeholder="Ex. Electronics"
                      />
                      
                      <label className="block mt-2 mb-2 text-sm font-medium text-gray-900">Phone Number</label>
                      <input
                        type="text"
                        value={newVendor.phoneNumber || ""}
                        onChange={(e) => handleNewVendorChange("phoneNumber", e.target.value)}
                        className="block w-full p-2.5 text-sm border border-gray-300 rounded-lg"
                        placeholder="Ex. +1234567890"
                      />
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 sm:ml-3 sm:w-auto"
                      onClick={addVendor}
                    >
                      Add Vendor
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => setOpenVendorModal(false)}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
